import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { formatNumber, formatPercentage } from "@/utils/numberFormat";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useCallback, useMemo } from "react";
import { Info } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import {
  KPIDisplayProps,
  KPIDisplayType
} from "@/components/controls/kpiDisplayTypes";
import WidgetSkeleton from "@/components/ui/widgetSkeleton";
import { cn } from "@/utils/utils";

const KPIDisplay = ({
  title,
  kpi,
  kpiTotal,
  kpiSecondary,
  kpiArray,
  info,
  type,
  isCurrency,
  isLoading,
  currencyCode,
  getKpiLabel
}: KPIDisplayProps & {
  /** Translated label for a KPI metadata key ("nrr", "grr", "unknown", …). */
  getKpiLabel: (key: string) => string;
}) => {
  const formatKpi = useCallback(
    (num: number, precision: number, currency: boolean, compact?: boolean) =>
      formatNumber(
        num,
        precision,
        currency,
        compact,
        undefined,
        undefined,
        undefined,
        currencyCode
      ),
    [currencyCode]
  );

  const kpiFormatted = useMemo(
    () =>
      type === KPIDisplayType.PERCENTAGE
        ? { currencySign: "", value: "", modifier: "" }
        : formatKpi(kpi, 2, isCurrency),
    [kpi, isCurrency, type, formatKpi]
  );
  const kpiFullFormatted = useMemo(
    () =>
      type === KPIDisplayType.PERCENTAGE
        ? { currencySign: "", value: "" }
        : formatKpi(kpi, 0, isCurrency, false),
    [kpi, isCurrency, type, formatKpi]
  );
  const isDualPercentage =
    type === KPIDisplayType.PERCENTAGE && kpiSecondary != null;

  const percentFromDenom = useMemo(
    () =>
      type === KPIDisplayType.PERCENTAGE && kpiTotal
        ? kpiTotal === 0
          ? 0
          : (kpi / kpiTotal) * 100
        : null,
    [kpi, kpiTotal, type]
  );

  if (isLoading) {
    return (
      <WidgetSkeleton className="rounded-lg min-w-50 max-w-90 min-h-32 max-h-32 grow basis-0 overflow-hidden" />
    );
  }

  return (
    <Card
      data-test="kpi-card"
      className={cn(
        "min-w-50 max-w-90 rounded-[12px] border-[0.5px] border-border-widget-card shadow-none",
        kpiArray && kpiArray.length > 1 && "h-33"
      )}
      style={{
        background:
          "linear-gradient(180deg, var(--surface-kpi-card-highlight-start) 0%, var(--surface-kpi-card-highlight-end) 100%), var(--surface-widget-card)"
      }}
    >
      <div className="animate-fadeIn h-full flex flex-col gap-2">
        <CardHeader
          data-test="kpi-card-header"
          className="flex flex-row flex-nowrap justify-between content-stretch items-start pt-3 !px-4"
        >
          <Text
            as="div"
            variant="bodySecondary"
            className="w-full text-start h-10 leading-5 break-words "
          >
            {title}
          </Text>
          {info && info !== "" && (
            <Tooltip data-test="kpi-info-tooltip" delayDuration={0}>
              <TooltipTrigger>
                <Text as="div" variant="lgBodyTertiary">
                  <IconContainer nativeStroke className="grow items-start">
                    <Info data-test="kpi-info-icon" />
                  </IconContainer>
                </Text>
              </TooltipTrigger>
              <TooltipContent side="bottom" maxWidth={20} enableMarkdown>
                {info}
              </TooltipContent>
            </Tooltip>
          )}
        </CardHeader>
        <CardContent
          data-test="kpi-card-content"
          className="flex flex-row flex-nowrap justify-start items-end content-stretch gap-x-3 w-full pb-8 !px-4"
        >
          <Tooltip data-test="kpi-display-tooltip">
            <TooltipTrigger className="w-full">
              <div
                data-test="kpi-display"
                className="flex flex-row flex-nowrap grow items-baseline justify-start h-10 py-0 w-full relative box-border"
              >
                {kpiArray && kpiArray.length > 1 ? (
                  <div className="flex flex-row items-center w-full gap-6 md:gap-4 pb-4">
                    {kpiArray.map((item, index) => (
                      <div
                        key={item.metadata?.key || index}
                        className="flex flex-row items-baseline gap-3 w-[50%]"
                      >
                        <Text
                          data-test={`${item.metadata?.key || "kpi"}-value`}
                          variant="title1H1"
                          className="ml-0"
                        >
                          {item.metadata?.currency === false
                            ? formatPercentage(item.value)
                            : (() => {
                                const formatted = formatKpi(
                                  item.value,
                                  1,
                                  item.metadata?.currency || false
                                );
                                return `${formatted.currencySign}${formatted.value}${formatted.modifier}`;
                              })()}
                        </Text>
                        <Text
                          data-test={`${item.metadata?.key || "kpi"}-label`}
                          variant="bodySecondary"
                          className="mt-0"
                        >
                          {getKpiLabel(item.metadata?.key || "unknown")}
                        </Text>
                      </div>
                    ))}
                  </div>
                ) : type === KPIDisplayType.PERCENTAGE ? (
                  isDualPercentage ? (
                    <div className="flex flex-row items-center w-full gap-x-6">
                      <div className="flex flex-row items-center gap-2 w-[50%]">
                        <Text
                          data-test="nrr-value"
                          variant="title1H1"
                          className="ml-0"
                        >
                          {formatPercentage(kpi)}
                        </Text>
                        <Text
                          data-test="nrr-label"
                          variant="bodySecondary"
                          className="mt-0"
                        >
                          {getKpiLabel("nrr")}
                        </Text>
                      </div>
                      <div className="flex flex-row items-center gap-2 w-[50%]">
                        <Text
                          data-test="grr-value"
                          variant="title1H1"
                          className="ml-0"
                        >
                          {formatPercentage(kpiSecondary || 0)}
                        </Text>
                        <Text
                          data-test="grr-label"
                          variant="bodySecondary"
                          className="mt-0"
                        >
                          {getKpiLabel("grr")}
                        </Text>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-row items-baseline gap-3">
                      <Text
                        data-test="kpi-display-value"
                        as="div"
                        variant="title1H1"
                        className="ml-[2px]"
                      >
                        {formatPercentage(percentFromDenom ?? 0)}
                      </Text>
                      {kpiTotal != null && (
                        <Text
                          data-test="kpi-total-display"
                          as="div"
                          variant="bodySecondary"
                          className="mt-[-8px]"
                        >
                          {(() => {
                            const num = formatKpi(
                              kpi ?? 0,
                              2,
                              isCurrency,
                              true
                            );
                            const den = formatKpi(
                              kpiTotal ?? 0,
                              2,
                              isCurrency,
                              true
                            );
                            return `${num.currencySign}${num.value}${num.modifier}/${den.currencySign}${den.value}${den.modifier}`;
                          })()}
                        </Text>
                      )}
                    </div>
                  )
                ) : (
                  <div className="flex flex-row items-baseline">
                    <Text
                      data-test="kpi-display-currency"
                      as="div"
                      variant="title2H1"
                      className="-translate-y-[2px]"
                    >
                      {kpiFormatted.currencySign}
                    </Text>
                    <Text
                      data-test="kpi-display-value"
                      as="div"
                      variant="title1H1"
                      className="ml-[2px]"
                    >
                      {kpiFormatted.value}
                    </Text>
                    <Text
                      data-test="kpi-display-value-modifier"
                      as="div"
                      variant="title1H1"
                      className="ml-0"
                    >
                      {kpiFormatted.modifier}
                    </Text>
                  </div>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div>
                {kpiArray && kpiArray.length > 1 ? (
                  kpiArray
                    .map((item) => {
                      const value =
                        item.metadata?.currency === false
                          ? formatPercentage(item.value)
                          : (() => {
                              const formatted = formatKpi(
                                item.value,
                                0,
                                item.metadata?.currency || false,
                                false
                              );
                              return `${formatted.currencySign}${formatted.value}${formatted.modifier}`;
                            })();
                      return `${getKpiLabel(item.metadata?.key || "unknown")}: ${value}`;
                    })
                    .join(", ")
                ) : isDualPercentage ? (
                  `${getKpiLabel("nrr")}: ${formatPercentage(kpi)}, ${getKpiLabel("grr")}: ${formatPercentage(kpiSecondary || 0)}`
                ) : (
                  <>
                    {type === KPIDisplayType.PERCENTAGE ? (
                      <>
                        {formatPercentage(percentFromDenom ?? 0)}
                        {kpiTotal &&
                          (() => {
                            const num = formatKpi(kpi, 0, isCurrency, false);
                            const den = formatKpi(
                              kpiTotal,
                              0,
                              isCurrency,
                              false
                            );
                            return ` (${num.currencySign}${num.value}${num.modifier} / ${den.currencySign}${den.value}${den.modifier})`;
                          })()}
                      </>
                    ) : (
                      <>
                        {kpiFullFormatted.currencySign}
                        {kpiFullFormatted.value}
                      </>
                    )}
                  </>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </CardContent>
      </div>
    </Card>
  );
};

export default KPIDisplay;
